import { cn } from "@ibada/ui"

export type ProgressTone = "primary" | "success" | "warning" | "destructive" | "muted"

const TONE_CLASS: Record<ProgressTone, string> = {
  primary: "bg-primary",
  success: "bg-emerald-500 dark:bg-emerald-400",
  warning: "bg-amber-500 dark:bg-amber-400",
  destructive: "bg-destructive",
  muted: "bg-muted-foreground/50",
}

export interface ProgressBarProps {
  /** 0–100; clamped. Values above 100 fill the track (use `tone="destructive"` to signal overage). */
  value: number
  tone?: ProgressTone
  className?: string
  "aria-label"?: string
}

/**
 * Compact horizontal progress/utilization bar. No shared Progress primitive exists in the design system,
 * so this is the dashboard's small, theme-aware track+fill used by budget/fund/pledge/reconciliation rows.
 */
export function ProgressBar({ value, tone = "primary", className, ...aria }: ProgressBarProps) {
  const clamped = Number.isFinite(value) ? Math.max(0, Math.min(100, value)) : 0
  return (
    <div
      role="progressbar"
      aria-valuenow={Math.round(clamped)}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={aria["aria-label"]}
      className={cn("h-1.5 w-full overflow-hidden rounded-full bg-muted", className)}
    >
      <div
        className={cn("h-full rounded-full transition-[width] duration-500 ease-out motion-reduce:transition-none", TONE_CLASS[tone])}
        style={{ width: `${clamped}%` }}
      />
    </div>
  )
}
